import { useCustomMutation } from '@refinedev/core';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  accountId: string;
  codeIndex: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void;
}

export function AssignCodeDialog({ accountId, codeIndex, open, onOpenChange, onAssigned }: Props) {
  const { mutate, isLoading } = useCustomMutation();
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setReference('');
      setNotes('');
    }
  }, [open]);

  const onSubmit = () => {
    if (codeIndex === null) return;
    if (reference.trim().length < 3) {
      toast.error('Referência precisa ter pelo menos 3 caracteres.');
      return;
    }
    mutate(
      {
        url: `/psn-accounts/${accountId}/recovery-codes/${codeIndex}/assign`,
        method: 'post',
        values: {
          assigned_reference: reference.trim(),
          notes: notes.trim() || undefined,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          toast.success(`Code ${codeIndex} atribuído.`);
          onOpenChange(false);
          onAssigned();
        },
        onError: (err) => {
          const code = (err as unknown as { errorCode?: string })?.errorCode;
          if (code === 'code_not_available') {
            toast.error('Code não está disponível.');
          } else {
            toast.error('Falha ao atribuir code.');
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Atribuir recovery code #{codeIndex}</DialogTitle>
          <DialogDescription>
            Referência fica gravada (texto livre). Útil pra você lembrar quem recebeu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reference">Referência *</Label>
            <Input
              id="reference"
              placeholder="ex: pedido #12345 ou cliente fulano@email.com"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notas (opcional)</Label>
            <Textarea
              id="notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={isLoading || reference.trim().length < 3}>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            Atribuir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
